import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Alert } from '@mui/material';
import { COLORS, UI } from '../config/colors';
import AIIcon from './AIIcon';

const ThinkingAccordion = ({ 
  title = "Show thinking", 
  content = "Analyzing your request and formulating a response...",
  isStreaming = true,
  responseContent = null,
  showThinking: initialShowThinking = false
}) => {
  const [showThinking, setShowThinking] = useState(initialShowThinking);
  const [rotation, setRotation] = useState(0);
  const [showResponse, setShowResponse] = useState(true);
  
  // Check if the content contains connection status messages
  const hasConnectionIssue = content && (
    content.includes("WebSocket connection") || 
    content.includes("connection_error") || 
    content.includes("connection_warning") ||
    content.includes("Connection idle") ||
    content.includes("Connecting to server") ||
    content.includes("Retrying connection")
  );
  
  // Add rotation animation to the AIIcon only when streaming
  useEffect(() => {
    let interval;
    if (isStreaming) {
      interval = setInterval(() => {
        setRotation(prev => (prev + 5) % 360);
      }, 50);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isStreaming]);
  
  return (
    <Box
      sx={{
        width: '800px',
        maxWidth: '95%',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${COLORS.border.light}`,
        backgroundColor: 'white',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* If we have a response and we're showing it, display that first */}
      {responseContent && showResponse && !showThinking && (
        <Box
          sx={{
            p: 2,
            backgroundColor: 'white',
          }}
        >
          <Typography
            sx={{
              color: COLORS.text.primary,
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {responseContent}
          </Typography>
        </Box>
      )}
      
      {/* Button to toggle thinking view */}
      <Button
        fullWidth
        onClick={() => {
          setShowThinking(!showThinking);
          // If we have a response and we're showing thinking, hide the response
          if (responseContent && !showThinking) {
            setShowResponse(false);
          } else if (responseContent && showThinking) {
            // If we're hiding thinking, show the response again
            setShowResponse(true);
          }
        }}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '12px 16px',
          textTransform: 'none',
          color: COLORS.text.secondary,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
          },
          borderTop: responseContent && showResponse ? `1px solid ${COLORS.border.light}` : 'none',
        }}
        startIcon={
          <Box 
            sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              mr: 1,
              transform: isStreaming ? `rotate(${rotation}deg)` : 'none',
              transition: 'transform 0.1s linear'
            }}
          >
            <AIIcon size={32} />
          </Box>
        }
        endIcon={
          <Box 
            component="span" 
            sx={{ 
              transform: showThinking ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out',
              marginLeft: 'auto',
            }}
          >
            ▼
          </Box>
        }
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {showThinking ? "Hide thinking" : title}
        </Typography>
      </Button>
      
      {/* Connection status alert - always show if there's a connection issue */}
      {hasConnectionIssue && (
        <Alert 
          severity="warning" 
          sx={{ 
            mt: 0, 
            borderRadius: 0,
            borderTop: `1px solid ${COLORS.border.light}`,
          }}
        >
          <Typography variant="body2">
            Connection issue detected. The system is attempting to reconnect.
          </Typography>
        </Alert>
      )}
      
      {/* Thinking content */}
      {showThinking && (
        <Box
          sx={{
            p: 2,
            backgroundColor: '#f8f9fa',
            borderTop: `1px solid ${COLORS.border.light}`,
          }}
        >
          {/* Parse the content to highlight connection-related messages */}
          {content.split('\n').map((line, index) => {
            const isConnectionMessage = 
              line.includes("WebSocket connection") || 
              line.includes("connection_error") || 
              line.includes("connection_warning") ||
              line.includes("Connection idle") ||
              line.includes("Connecting to server") ||
              line.includes("Retrying connection");
              
            return (
              <Typography
                key={index}
                sx={{
                  color: isConnectionMessage ? COLORS.error.main : COLORS.text.secondary,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  fontStyle: isConnectionMessage ? 'normal' : 'italic',
                  fontWeight: isConnectionMessage ? 'bold' : 'normal',
                  mb: 0.5
                }}
              >
                {line}
              </Typography>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ThinkingAccordion;
